
import { supabase } from '../lib/supabase';

interface CreateNotificationParams {
  userId: string;
  type: 'booking_update' | 'message' | 'property_update' | 'system';
  title: string;
  message: string;
  relatedId?: string;
}

export const createNotification = async ({
  userId,
  type,
  title,
  message,
  relatedId
}: CreateNotificationParams) => {
  const { error } = await supabase.from('notifications').insert({
    user_id: userId,
    type,
    title,
    message,
    related_id: relatedId || null
  });

  if (error) {
    console.error('Error creating notification:', error);
    return false;
  }
  return true;
};

export const notifyBookingStatusChange = async (
  userId: string,
  propertyTitle: string,
  newStatus: string,
  bookingId: string
) => {
  const statusMessages: Record<string, { title: string; message: string }> = {
    confirmed: {
      title: 'Booking Confirmed! 🎉',
      message: `Your viewing for "${propertyTitle}" has been confirmed. Check your dashboard for details.`
    },
    cancelled: {
      title: 'Booking Cancelled',
      message: `Your viewing for "${propertyTitle}" has been cancelled.`
    },
    completed: {
      title: 'Viewing Completed',
      message: `Your viewing for "${propertyTitle}" has been marked as completed. We hope it went well!`
    },
    pending: {
      title: 'Booking Received',
      message: `Your viewing request for "${propertyTitle}" has been received. The agent will confirm shortly.`
    }
  };

  const notification = statusMessages[newStatus] || {
    title: 'Booking Update',
    message: `Your booking for "${propertyTitle}" has been updated to: ${newStatus}`
  };

  return createNotification({
    userId,
    type: 'booking_update',
    title: notification.title,
    message: notification.message,
    relatedId: bookingId
  });
};

export const notifyNewMessage = async (
  userId: string,
  senderName: string,
  messagePreview: string,
  relatedId?: string
) => {
  return createNotification({
    userId,
    type: 'message',
    title: `New message from ${senderName}`,
    message: messagePreview.length > 100 ? messagePreview.substring(0, 100) + '...' : messagePreview,
    relatedId
  });
};

export const notifyPropertyUpdate = async (
  userId: string,
  propertyTitle: string,
  updateType: 'approved' | 'rejected' | 'price_change' | 'status_change',
  propertyId: string
) => {
  const updateMessages: Record<string, { title: string; message: string }> = {
    approved: {
      title: 'Property Approved! ✅',
      message: `Your property "${propertyTitle}" has been approved and is now live on the platform.`
    },
    rejected: {
      title: 'Property Needs Review',
      message: `Your property "${propertyTitle}" requires some changes before approval. Please check the details.`
    },
    price_change: {
      title: 'Price Updated',
      message: `The price for "${propertyTitle}" has been updated.`
    },
    status_change: {
      title: 'Property Status Changed',
      message: `The status of "${propertyTitle}" has been updated.`
    }
  };

  const notification = updateMessages[updateType];

  return createNotification({
    userId,
    type: 'property_update',
    title: notification.title,
    message: notification.message,
    relatedId: propertyId
  });
};

export const notifySystem = async (
  userId: string,
  title: string,
  message: string
) => {
  return createNotification({
    userId,
    type: 'system',
    title,
    message
  });
};
